import { z } from 'zod'

export const ONBOARDING_TEXT_MAX_LENGTH = 256

const text = z.string().max(ONBOARDING_TEXT_MAX_LENGTH)
const id = z.string().min(1).max(256)
const date = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine((value) => {
    const parsed = new Date(`${value}T00:00:00Z`)
    return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value
  }, 'Invalid calendar date')
const range = z
  .object({ start: date, end: date })
  .strict()
  .refine((r) => r.start <= r.end)
const account = z
  .object({
    id,
    bankId: id,
    institutionName: text,
    label: text,
    type: text,
    checked: z.boolean(),
    nickname: text,
  })
  .strict()
const bankAccount = z
  .object({
    accountId: id,
    itemId: id,
    accountName: text,
    accountNumber: text,
    accountType: text,
    accountSubtype: text,
    institutionName: text,
  })
  .strict()
const choice = z.object({ option: text, startDate: text, endDate: text }).strict()

export const OnboardingNavigationSchema = z
  .object({
    direction: z.enum(['forward', 'back']),
    from: z.string().min(1).max(512),
    to: z.string().min(1).max(512),
  })
  .strict()

export const OnboardingJourneySummarySchema = z
  .object({
    status: z.enum(['in_progress', 'completed']),
    attemptNumber: z.number().int().positive(),
    saveCount: z.number().int().nonnegative(),
    forwardCount: z.number().int().nonnegative(),
    backCount: z.number().int().nonnegative(),
    banksConnected: z.number().int().nonnegative(),
    accountsSelected: z.number().int().nonnegative(),
    template: text,
    startedAt: z.string().datetime(),
    completedAt: z.string().datetime().optional(),
    durationSeconds: z.number().int().nonnegative().optional(),
    lastStep: z.string().max(512).optional(),
  })
  .strict()

// Only display metadata and user choices belong here, never credentials or rows.
export const OnboardingDraftSchema = z
  .object({
    referralSource: text,
    selectedAccounts: z.array(account).max(200),
    connectedBanks: z
      .array(
        z
          .object({
            id,
            institutionName: text,
            accounts: z.array(account).max(200),
          })
          .strict()
      )
      .max(50),
    pendingBankAccounts: z.array(bankAccount).max(200),
    // Banks skipped for this spreadsheet remain connected to Plaid. Persist the
    // choice so a reload or Google OAuth return cannot stage them again.
    //
    // Bound client-submitted IDs before writing the draft. Keep the limit aligned
    // with pendingBankAccounts while allowing more IDs than the 50-bank UI cap.
    excludedBankIds: z.array(id).max(200).optional(),
    syncDateConfigByBank: z.record(
      id,
      z
        .object({
          mode: z.enum(['all', 'individual']),
          globalOption: text,
          globalStartDate: text,
          globalEndDate: text,
          perAccount: z.record(id, choice),
        })
        .strict()
    ),
    selectedTemplate: text,
    isAddingAnotherBank: z.boolean(),
    resolvedRanges: z.record(z.string().max(600), range).default({}),
  })
  .strict()

export const OnboardingSnapshotSchema = z
  .object({
    choices: OnboardingDraftSchema,
    spreadsheetId: id,
    completedAt: z.string().datetime(),
    journeySummary: OnboardingJourneySummarySchema.optional(),
  })
  .strict()

export const OnboardingRecordSchema = z
  .object({
    schemaVersion: z.literal(1),
    revision: z.number().int().nonnegative(),
    generation: z.number().int().nonnegative(),
    onboardingComplete: z.boolean(),
    legacy: z.boolean(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    draft: OnboardingDraftSchema.nullable(),
    completedSnapshot: OnboardingSnapshotSchema.nullable(),
    // Optional so records written before journey summaries were introduced remain readable.
    journeySummary: OnboardingJourneySummarySchema.optional(),
  })
  .strict()

// Routing must not depend on historical choices being readable. Unknown status
// or schema versions still fail closed; only known routing fields can be used.
export const OnboardingRoutingSchema = OnboardingRecordSchema.pick({
  schemaVersion: true,
  revision: true,
  generation: true,
  onboardingComplete: true,
}).strip()

export const OnboardingResponseSchema = z
  .object({
    record: OnboardingRecordSchema.nullable(),
    onboardingComplete: z.boolean(),
    unavailableDetails: z
      .object({
        revision: z.number().int().nonnegative(),
        generation: z.number().int().nonnegative(),
      })
      .strict()
      .optional(),
  })
  .strict()
  .refine((value) => !value.unavailableDetails || value.record === null)
  .refine((value) => !value.record || value.record.onboardingComplete === value.onboardingComplete)

const version = { expectedRevision: z.number().int().nonnegative() }
export const OnboardingMutationSchema = z.discriminatedUnion('action', [
  z
    .object({
      ...version,
      action: z.literal('save'),
      draft: OnboardingDraftSchema,
      navigation: OnboardingNavigationSchema.optional(),
    })
    .strict(),
  z
    .object({
      ...version,
      action: z.literal('complete'),
      draft: OnboardingDraftSchema,
      spreadsheetId: id,
    })
    .strict(),
  z.object({ ...version, action: z.literal('reset') }).strict(),
])

export type OnboardingDraft = z.infer<typeof OnboardingDraftSchema>
export type OnboardingNavigation = z.infer<typeof OnboardingNavigationSchema>
export type OnboardingJourneySummary = z.infer<typeof OnboardingJourneySummarySchema>
export type OnboardingRecord = z.infer<typeof OnboardingRecordSchema>
export type OnboardingMutation = z.infer<typeof OnboardingMutationSchema>
export type OnboardingResponse = z.infer<typeof OnboardingResponseSchema>
